import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";

import { BarChart } from "src/components/charts/bar_chart";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { constructTitle } from "src/pages/reports/construct_notes";
import { DomainsCircle } from "src/pages/reports/fsci/all/graphics/domains_circle";
import { isNotNil } from "src/utils/type_guards";
import { isConstructHidden } from "src/config/hiddenConstructs";

import { meta } from "./meta";

/*
 * Types
 */

export type PurposeProps = {
    readonly reports: Array<any>;
};

/*
 * Main
 */

export const Purpose = (props: PurposeProps): React.ReactElement => {
    const { reports } = props;

    return (
        <div className="main-report-content-wrap">
            <div className="single-domain-result-top-wrap">
                <DomainsCircle activeDomain="purpose" inline />

                <PageTitle>Purpose</PageTitle>
                <Body>
                    A Christian perspective on education provides the <em>telos</em>, or purposeful aim, of Christian
                    schooling. The FSCI identified a commitment on the part of all school constituencies to the central
                    purposes of education as strongly connected with flourishing outcomes.
                </Body>
                <Body>
                    Many of the distinctives of Christian education—meaning what is unique to Christian schools’
                    missions, values, and expected outcomes, as opposed to other sectors of education—fall within this
                    domain. FSCI data analysis identified constructs related to the domain of Purpose for all seven
                    survey groups (students; parents; alumni; teachers; leaders/administrators; support staff; and board
                    members), below.
                </Body>
                <Body>
                    <em>
                        Benchmark data is not included for the purpose of scoring or ranking your school against other
                        schools; rather, it is provided to help your school a) gauge priorities and b) track change over
                        subsequent administrations of the FSCI at your school.
                    </em>
                </Body>
                <Separator />
            </div>
            {pipe(
                Object.keys(reports[0].node.results.raw.purpose),
                A.filter((construct) => !isConstructHidden(construct)),
                A.map((construct) => (
                    <div className="an-opportunity-item domain-result" key={construct}>
                        {pipe(
                            R.lookup(construct, meta as Record<string, Opportunity>),
                            O.map((m) => (
                                <>
                                    <SectionTitle>{constructTitle(m)}</SectionTitle>
                                    <Body>{m.prologue}</Body>
                                    <BarChart
                                        data={reports
                                            .map((r) => ({
                                                label: r.node.year,
                                                value:
                                                    (r.node.results.raw.purpose as Record<string, number>)[construct] *
                                                    100,
                                            }))
                                            .concat({
                                                label: "FSCI Benchmark",
                                                value:
                                                    (
                                                        reports[0].node.results.benchmarks.purpose as Record<
                                                            string,
                                                            number
                                                        >
                                                    )[construct] * 100,
                                            })}
                                    />
                                    {isNotNil(m.extra) && <Body className="small-domain-text">{m.extra}</Body>}
                                </>
                            )),
                            O.getOrElse(() => <></>),
                        )}
                    </div>
                )),
            )}
        </div>
    );
};
